import type { ReactNode } from "react";
import { cn } from "../ui/cn";

// ---------------------------------------------------------------------------
// Franja de indicadores de las consolas operativas de personal
// (CC-CONSOLE#1595): tarjetas compactas con icono, número grande y una línea
// de contexto. Sólo tokens semánticos: se ve igual en claro y oscuro.
// ---------------------------------------------------------------------------

export const KPI_TONES = {
  neutral: "bg-subtle text-content-secondary",
  primary: "bg-primary-500/15 text-primary-500",
  success: "bg-success/15 text-success",
  warning: "bg-warning/15 text-warning",
  error: "bg-error/15 text-error",
  info: "bg-info/15 text-info",
  violet: "bg-violet-500/15 text-violet-500",
} as const satisfies Record<string, string>;

export type KpiTone = keyof typeof KPI_TONES;

export interface KpiItem {
  id: string;
  label: string;
  value: ReactNode;
  icon: string;
  tone?: KpiTone;
  /** Línea pequeña bajo el número (p. ej. «de 18 activos»). */
  hint?: ReactNode;
  /** 0–1: barra de avance bajo el número. */
  progress?: number | null;
  onClick?: () => void;
  active?: boolean;
}

interface Props {
  items: KpiItem[];
  loading?: boolean;
  className?: string;
}

export default function KpiStrip({ items, loading = false, className }: Props) {
  return (
    <div className={cn("grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6", className)} data-testid="kpi-strip">
      {items.map((k) => {
        const tone = KPI_TONES[k.tone ?? "neutral"];
        const body = (
          <>
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-[11px] font-medium uppercase tracking-wide text-content-secondary">{k.label}</span>
              <span className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-lg", tone)}>
                <i className={cn(k.icon, "text-sm")} aria-hidden="true" />
              </span>
            </div>
            <div className="mt-1 text-2xl font-bold leading-tight text-content" data-testid={`kpi-${k.id}`}>
              {loading ? <span className="inline-block h-6 w-12 animate-pulse rounded bg-subtle" /> : k.value}
            </div>
            {k.progress != null && !loading && (
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-subtle">
                <div className="h-full rounded-full bg-primary-500" style={{ width: `${Math.round(Math.min(1, Math.max(0, k.progress)) * 100)}%` }} />
              </div>
            )}
            {k.hint && <div className="mt-1 truncate text-[11px] text-content-muted">{k.hint}</div>}
          </>
        );
        const cls = cn(
          "rounded-xl border border-line bg-card p-3 text-left shadow-sm",
          k.onClick && "transition hover:border-primary-500/60",
          k.active && "ring-2 ring-primary-500",
        );
        return k.onClick ? (
          <button key={k.id} type="button" onClick={k.onClick} className={cls} aria-pressed={k.active ?? false}>
            {body}
          </button>
        ) : (
          <div key={k.id} className={cls}>
            {body}
          </div>
        );
      })}
    </div>
  );
}
